import type { IStationsLineRef } from '../../../api/types';
import type { IStationsLine } from '../../../data/istationsLines';

/**
 * 「運行情報の参照元（アイステイションズ路線）」の複数選択 UI。
 *
 * 接続線遅延コンテンツの part1 / part2 の **両方から** 使う共通部品。
 * - part1 (`connecting_line_delay`): `properties.istations_lines`
 *   (`ConnectingLineDelayFields.tsx` から利用)
 * - part2 (`connecting_line_delay_by_station`): `properties.lines[].istations_lines`
 *   (`ConnectingLineBlock.tsx` から利用)
 *
 * 【設計上の重要な制約: ATOS の route_id と連動させない】
 *
 * 本コンポーネントは **意図的に ATOS 線区マスタ (`RouteMaster` / `routes`) を参照しない**。
 * 接続線遅延の判定対象は ATOS の線名コードだが、運行情報の参照元として紐付ける
 * アイステイションズ路線は別系統のコードであり、「ATOS の route_id と一致しない
 * 路線を運用側で自由に選べること」が要件である。
 * props に `routes` を受け取らない設計にすることで、将来的にうっかり連動ロジック
 * (ATOS 選択に応じた絞り込み等) が混入することを構造的に防いでいる。
 *
 * 選択肢は `fetchIStationsLines()` (`istations-lines.json`) 由来のマスタのみで構成する。
 */
export function IStationsLinesSelect({
  testIdPrefix,
  ariaLabelPrefix,
  options,
  value,
  maxItems,
  onChange,
}: {
  /**
   * data-testid の接頭辞。part1 / part2 で同一画面に複数出ても一意になるよう
   * 呼び出し側で異なる値を渡す (例: `connecting-istations-lines` /
   * `connecting-line-istations-lines-0`)。
   */
  testIdPrefix: string;
  /**
   * aria-label の接頭辞。E2E / スクリーンリーダー向けに、どの接続線に属する
   * 選択欄かを区別できる文言を渡す (例: `接続線#1 `)。
   */
  ariaLabelPrefix?: string;
  /** 選択肢 (アイステイションズ路線マスタ)。取得失敗時は空配列でよい。 */
  options: readonly IStationsLine[];
  /** 現在選択されているアイステイションズ路線 (順序は入力順)。 */
  value: readonly IStationsLineRef[];
  /**
   * 選択できる上限件数。既定 10 件で、サーバ側 `MAX_ISTATIONS_LINES` と一致させる
   * (上限に達したら追加プルダウンを disabled にして 400 を事前に防ぐ)。
   */
  maxItems?: number;
  onChange: (next: IStationsLineRef[]) => void;
}): React.JSX.Element {
  const limit = maxItems ?? 10;
  const selected = Array.isArray(value) ? value : [];
  const isFull = selected.length >= limit;

  // すでに選択済みの route_id は追加プルダウンから除外する
  // (重複はサーバ側で 400 になるため、UI 側でも選べないようにする)。
  const selectedIds = new Set(selected.map((l) => l.route_id));
  const addableOptions = options.filter((o) => !selectedIds.has(o.route_id));

  /** route_id を追加する。マスタから route_name をスナップショットとして持たせる。 */
  const addLine = (routeId: string): void => {
    if (!routeId || selectedIds.has(routeId) || isFull) return;
    const master = options.find((o) => o.route_id === routeId);
    const entry: IStationsLineRef = master?.route_name
      ? { route_id: routeId, route_name: master.route_name }
      : { route_id: routeId };
    onChange([...selected, entry]);
  };

  /** index 番目の選択を取り除く。 */
  const removeAt = (index: number): void => {
    const next = selected.slice();
    next.splice(index, 1);
    onChange(next);
  };

  return (
    <div className="istations-lines-select" data-testid={testIdPrefix}>
      <span>運行情報の参照元（アイステイションズ路線）</span>

      {/* 選択済み一覧。0 件のときは空メッセージを出す (未設定 = 紐付けなし)。 */}
      {selected.length === 0 ? (
        <div
          className="empty-state"
          style={{ padding: '4px 0', fontSize: 12, color: '#555' }}
          data-testid={`${testIdPrefix}-empty`}
        >
          未設定です。下のプルダウンから追加してください（未設定でも保存できます）。
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: '4px 0', padding: 0 }}>
          {selected.map((line, i) => {
            // 表示名はマスタの最新名称 → 保存済みスナップショット → route_id の順で解決する。
            // マスタから削除された路線でも既存の設定値が画面から消えないようにする。
            const master = options.find((o) => o.route_id === line.route_id);
            const displayName = master?.route_name || line.route_name || line.route_id;
            const isUnknown = master === undefined;
            return (
              <li
                key={`${line.route_id}-${i}`}
                data-testid={`${testIdPrefix}-item-${i}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 0' }}
              >
                <span>
                  {displayName}
                  <code style={{ marginLeft: 6, fontSize: 11, color: '#555' }}>
                    {line.route_id}
                  </code>
                  {isUnknown ? (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#a00' }}>
                      (マスタ未登録)
                    </span>
                  ) : null}
                </span>
                <button
                  type="button"
                  className="danger"
                  aria-label={`${ariaLabelPrefix ?? ''}アイステイションズ路線 ${displayName} を削除`}
                  data-testid={`${testIdPrefix}-remove-${i}`}
                  onClick={() => removeAt(i)}
                >
                  × 削除
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        追加用プルダウン。選択したら即 onChange して value を空に戻す
        (「追加操作」専用のセレクトなので選択状態は保持しない)。
      */}
      <select
        aria-label={`${ariaLabelPrefix ?? ''}アイステイションズ路線を追加`}
        data-testid={`${testIdPrefix}-add-select`}
        value=""
        disabled={isFull || addableOptions.length === 0}
        onChange={(e) => addLine(e.target.value)}
      >
        <option value="">
          {isFull
            ? `(上限 ${limit} 件に達しています)`
            : addableOptions.length === 0
              ? '(追加できる路線がありません)'
              : '(追加する路線を選択)'}
        </option>
        {addableOptions.map((o) => (
          <option key={o.route_id} value={o.route_id}>
            {o.route_name}（{o.route_id}）
          </option>
        ))}
      </select>

      <span className="hint" style={{ fontSize: 12, color: '#555' }}>
        接続線の遅延情報を取得する参照元として、アイステイションズの路線名称から複数選択できます。
        接続線 route_id（ATOS）とは独立した設定で、
        <strong>一致しない路線も自由に選べます</strong>（最大 {limit} 件）。
      </span>
    </div>
  );
}
